/**
 * Hook that loads the filter-operator catalog for the report builder.
 *
 * The catalog is keyed by data type (`text`, `number`, `enum`, `bool`,
 * `date`, `datetime`) and is cached in module scope for the lifetime of
 * the page so the builder can switch fields without re-fetching.
 */

import { useEffect, useState } from 'react';
import {
  reportingAPI,
  type OperatorCatalogResponse,
} from '../../../lib/api/reporting';

let cachedCatalog: OperatorCatalogResponse | null = null;
let inflight: Promise<OperatorCatalogResponse> | null = null;

interface UseOperatorCatalogResult {
  catalog: OperatorCatalogResponse | null;
  loading: boolean;
}

/**
 * Returns the filter-operator catalog used by the builder's filter editor.
 *
 * @returns `{ catalog, loading }` — `catalog` is `null` until the first
 *   response arrives.
 */
export function useOperatorCatalog(): UseOperatorCatalogResult {
  const [catalog, setCatalog] = useState<OperatorCatalogResponse | null>(cachedCatalog);
  const [loading, setLoading] = useState<boolean>(cachedCatalog === null);

  useEffect(() => {
    if (cachedCatalog) {
      setCatalog(cachedCatalog);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    if (!inflight) {
      inflight = reportingAPI
        .getOperatorCatalog()
        .then((res) => {
          cachedCatalog = res.data;
          return res.data;
        })
        .finally(() => {
          inflight = null;
        });
    }
    inflight
      .then((data) => {
        if (!cancelled) {
          setCatalog(data);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { catalog, loading };
}
